import {
  CEFR_LEVELS,
  CURRICULUM_LANGUAGES,
  CURRICULUM_PHASES,
  CURRICULUM_ROLES,
  CurriculumChanged,
  type CurriculumListItem,
  type CurriculumRole,
  CurriculumTags,
  DocumentId,
  hasUnreleasedDrift,
  type LanguageReadiness,
  missingInventories,
  missingInventoryMessage,
} from "@curriculum-studio/core";
import { DateTime, Either, ParseResult, Schema } from "effect";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router";
import { AsyncWorkIndicator, RowGenerationProgress } from "./async-work-indicator.tsx";
import { useGateActions } from "./gate-context.tsx";
import { languagePackUploadActions } from "./language-pack-upload-actions.ts";
import { fetchLaunchAccess, type ViewerRoleWire } from "./launch-actions.ts";
import { PhaseTracker } from "./phase-tracker.tsx";
import { type UnitPlanParams, unitPlanActions } from "./unit-plan-actions.ts";

// The curricula index. Its whole job is to answer three questions at a glance — which document is the
// real one for a level, how far through production it is, and what learners are currently seeing — so
// it is organised the way the corpus actually is: LANGUAGE PAIR → CEFR LEVEL → the canonical document
// with its experiments nested beneath.
//
// Two axes carry state, and they are shown in deliberately different ways so they never compete:
//   ROLE is carried by the LAYOUT (the canonical row sits first and raised, experiments nest under it
//     recessed), which is why its filter is colourless.
//   PHASE is carried by COLOUR, because it has no other channel — and the colour encodes the step's
//     STATE (not started / in progress / done / returned to) rather than which stage it is. That is
//     what keeps "structure done" unmistakable from "structure being redone".
// The brand orange is reserved for actions, per the design system — never for state.

const ROW_BUTTON =
  "inline-flex h-8 shrink-0 items-center rounded-md border border-zinc-200 bg-white px-3 text-xs font-medium text-zinc-900 shadow-xs transition-colors hover:bg-zinc-100 focus-visible:outline-hidden focus-visible:ring-1 focus-visible:ring-brand disabled:pointer-events-none disabled:opacity-50";

const ICON_BUTTON =
  "grid h-7 w-7 shrink-0 place-items-center rounded-md border border-zinc-200 bg-white text-zinc-600 shadow-xs transition-colors hover:bg-zinc-100 hover:text-zinc-900 focus-visible:outline-hidden focus-visible:ring-1 focus-visible:ring-brand disabled:pointer-events-none disabled:opacity-50";

// Publishing is the one row action that takes the brand, because it is the one that changes what
// learners see.
const ICON_BUTTON_BRAND =
  "grid h-7 w-7 shrink-0 place-items-center rounded-md border border-brand bg-white text-brand shadow-xs transition-colors hover:bg-brand hover:text-white focus-visible:outline-hidden focus-visible:ring-1 focus-visible:ring-brand disabled:pointer-events-none disabled:opacity-50";

const TAG_SELECT =
  "h-8 rounded-md border border-zinc-200 bg-white px-2 text-xs text-zinc-900 focus-visible:outline-hidden focus-visible:ring-1 focus-visible:ring-brand disabled:opacity-50";

// The row actions are ICONS: the action is obvious from context (an experiment promotes, a canonical
// clones, a drifted document publishes), and a column of short verbs competes with the titles for
// attention. Each carries a `title` so the meaning is never guessed at, and an `aria-label` so the
// control is named for assistive tech.
const ICON = "h-3.5 w-3.5";

const CloneIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    className={ICON}
    aria-hidden="true"
  >
    <path d="M9 3h9a2 2 0 0 1 2 2v9" strokeLinecap="round" />
    <rect x="4" y="8" width="12" height="13" rx="2" />
  </svg>
);

const PromoteIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    className={ICON}
    aria-hidden="true"
  >
    <path d="M12 19V5M5 12l7-7 7 7" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const PublishIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    className={ICON}
    aria-hidden="true"
  >
    <path
      d="M12 16V4M7 9l5-5 5 5M4 17v2a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const languageName = (code: string): string =>
  CURRICULUM_LANGUAGES.find((l) => l.code === code)?.name ?? code;

// The L1 is the learner's locale, so its shorthand is the alpha-2 code (EN) — not the Babbel L2 code
// (QAE) the tag happens to store. The L2 side of the pair keeps its Babbel code, which is the
// identifier every other surface joins on.
const l1Shorthand = (code: string): string =>
  CURRICULUM_LANGUAGES.find((l) => l.code === code)?.alpha2 ?? code;

// Levels sort by their position in the closed CEFR set, never lexically — a string sort puts B1 before
// A2.2, which is exactly backwards for a ladder.
const levelRank = (level: string): number => {
  const index = (CEFR_LEVELS as readonly string[]).indexOf(level);
  return index < 0 ? CEFR_LEVELS.length : index;
};

// Relative time is what a list is scanned for; the absolute date lives on the document page.
//
// Counted in LOCAL CALENDAR DAYS, not 24-hour blocks: "today" has to mean today, so 23:00 yesterday
// reads "yesterday" rather than "today" and 01:00 today reads "today" rather than "1d ago".
const DAY_MS = 1000 * 60 * 60 * 24;
const startOfLocalDay = (ms: number): number => {
  const date = new Date(ms);
  date.setHours(0, 0, 0, 0);
  return date.getTime();
};
const since = (at: DateTime.Utc): string => {
  const days = Math.round(
    (startOfLocalDay(Date.now()) - startOfLocalDay(DateTime.toEpochMillis(at))) / DAY_MS,
  );
  if (days <= 0) return "today";
  if (days === 1) return "yesterday";
  if (days < 30) return `${days}d ago`;
  if (days < 365) return `${Math.round(days / 30)}mo ago`;
  return `${(days / 365).toFixed(1)}y ago`;
};

// ─── Version column ───────────────────────────────────────────────────────────
//
// Three distinct states, because they mean genuinely different things:
//   never published            → a plain version
//   published and current      → green: what is live IS what is latest
//   published with head ahead  → the pin in green, head in the ACTIVE stage's colour, so an amber head
//                                means "the in-flight work is a rework" and a green one means
//                                "ordinary unreleased drift"
const VersionCell = ({ item }: { readonly item: CurriculumListItem }) => {
  const pin = item.publishedVersion;
  const ahead = hasUnreleasedDrift(pin, item.docVersion);
  const headClass =
    item.reworkPhase !== undefined
      ? "text-amber-800"
      : item.phase === "published"
        ? "text-emerald-800"
        : "text-sky-800";
  return (
    <span className="flex shrink-0 flex-col items-end leading-tight">
      <span className="text-[10px] font-medium uppercase tracking-wide text-zinc-400">
        {ahead ? "Live / head" : pin !== undefined ? "Live" : "Version"}
      </span>
      <span className="text-[13px] tabular-nums text-zinc-600">
        {ahead ? (
          <>
            <span className="font-medium text-emerald-800">v{pin}</span>
            <span className={`font-medium ${headClass}`}> → v{item.docVersion}</span>
          </>
        ) : pin !== undefined ? (
          <span className="font-medium text-emerald-800">v{pin}</span>
        ) : (
          `v${item.docVersion}`
        )}
      </span>
    </span>
  );
};

// ─── Rows ─────────────────────────────────────────────────────────────────────

const ROLE_LABEL: Record<CurriculumRole, string> = {
  canonical: "Canonical",
  experiment: "Experiment",
  archived: "Archived",
};

// U7 (CON-1316): a generating-origin document has no `title` yet — that only lands with the
// generator's own commit — so a "pending"/"failed" row falls back to the identity it WAS started
// with, read off `tags` rather than invented. `l1` is optional on `CurriculumTags` (a document tagged
// before the pair existed, or one an Expert has not paired yet), so a missing L1 reads as `?` rather
// than silently dropping half the label.
const fallbackLabel = (item: CurriculumListItem): string => {
  if (item.tags === undefined) return item.documentId;
  return `${item.tags.level} · ${item.tags.l1 ?? "?"} → ${item.tags.language}`;
};

// The one spinner for "a generation run is authoring this document right now" — same visual
// language as the hover indicator's own spinner (`async-work-indicator.tsx`), kept as a SEPARATE,
// smaller component rather than imported from there: this one replaces the row's title outright
// (no hover needed to see it), the hover indicator augments it with task-level detail.
const PendingRowSpinner = () => (
  <span className="relative flex h-4 w-4 shrink-0 items-center justify-center" aria-hidden="true">
    <span className="absolute inset-0 animate-spin rounded-full border-2 border-brand/25 border-t-brand" />
  </span>
);

const FailedRowGlyph = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    className="h-4 w-4 shrink-0 text-red-600"
    aria-hidden="true"
  >
    <circle cx="12" cy="12" r="9" />
    <path d="M9 9l6 6M15 9l-6 6" strokeLinecap="round" />
  </svg>
);

// The row title, generalised over the three states a generating-origin document can be in.
// `generationStatus` is absent for every ordinary document (imported, or a generation run that has
// already landed its title) — this is the ONLY branch point, and it is what stands in for the title
// text, never what decides whether the row exists: a failed run's row stays in the list exactly
// where its (still real, still openable) document sits.
//
// DEVIATION (flagged, not silent): `CurriculumListItem` carries no failure-reason field (confirmed
// by reading `packages/core/src/tool-args.ts`'s schema in full) — the failed state below is
// necessarily generic ("Generation failed") rather than naming what went wrong. Adding that reason
// would mean widening the list-item wire schema and the server-side fold that produces it, which is
// out of this unit's scope.
const RowTitle = ({ item }: { readonly item: CurriculumListItem }) => {
  if (item.generationStatus === "pending") {
    return (
      <span className="flex items-center gap-2" data-testid="curriculum-row-pending">
        <PendingRowSpinner />
        <span>{fallbackLabel(item)}</span>
      </span>
    );
  }
  if (item.generationStatus === "failed") {
    return (
      <span className="flex items-center gap-2" data-testid="curriculum-row-failed">
        <FailedRowGlyph />
        <span>Generation failed — {fallbackLabel(item)}</span>
      </span>
    );
  }
  return <>{item.title ?? item.documentId}</>;
};

const CurriculumRow = ({
  item,
  canonical,
  onClone,
  onPromote,
  onPublish,
  busy,
}: {
  readonly item: CurriculumListItem;
  readonly canonical: boolean;
  readonly onClone: (sourceId: DocumentId) => void;
  readonly onPromote: (item: CurriculumListItem) => void;
  readonly onPublish: (documentId: DocumentId) => void;
  readonly busy: boolean;
}) => {
  const id = DocumentId.make(item.documentId);
  const fileName = item.documentId.replace(/^curricula\//, "");
  const ahead = hasUnreleasedDrift(item.publishedVersion, item.docVersion);
  // CON-1316 cancel: "anyone with permission to start can cancel any run" — no per-row check of who
  // started it, mirroring the server's own role gate (`requireLaunchAccess` covers both). Local to
  // this row (not lifted to the list) since every row's cancel state is independent.
  const [cancelling, setCancelling] = useState(false);
  const [cancelError, setCancelError] = useState<string | undefined>(undefined);
  const handleCancel = () => {
    if (cancelling) return;
    setCancelError(undefined);
    setCancelling(true);
    unitPlanActions
      .cancel(id)
      .then((result) => {
        if (result._tag === "failed") setCancelError(result.message);
      })
      .finally(() => setCancelling(false));
  };
  // The action is always whatever moves the work forward: promote an experiment, release unreleased
  // drift, otherwise branch a new experiment. It carries the brand — the one place colour means "do".
  const action = !canonical
    ? {
        icon: <PromoteIcon />,
        label: "Promote to canonical",
        brand: false,
        run: () => onPromote(item),
      }
    : ahead && item.reworkPhase === undefined
      ? {
          icon: <PublishIcon />,
          label: `Publish v${item.docVersion} — replaces the live v${item.publishedVersion}`,
          brand: true,
          run: () => onPublish(id),
        }
      : {
          icon: <CloneIcon />,
          label: "Clone for experiment",
          brand: false,
          run: () => onClone(id),
        };
  return (
    // The whole card opens the document. Rather than a click handler on the row — which would leave
    // the card unreachable by keyboard and unopenable in a new tab — the title's own link stretches an
    // invisible overlay across the card (`after:inset-0`). One link, still the accessible name, but the
    // hit area is the card. The row's action button sits above the overlay so it stays clickable.
    <li
      className={
        canonical
          ? `relative flex flex-col gap-1.5 rounded-2xl bg-white px-4 py-3 shadow-[0_2px_8px_rgba(0,0,0,0.06)] transition-colors hover:bg-zinc-50 ${
              item.reworkPhase !== undefined ? "ring-2 ring-amber-200" : ""
            }`
          : "relative ml-6 flex flex-col gap-1.5 rounded-2xl border border-zinc-200 bg-zinc-50 px-4 py-3 transition-colors hover:bg-zinc-100"
      }
    >
      <span className="flex items-center gap-3">
        <Link
          className={`min-w-0 flex-1 truncate font-serif tracking-[-0.01em] text-zinc-900 after:absolute after:inset-0 after:rounded-2xl after:content-[''] focus-visible:outline-hidden focus-visible:after:ring-1 focus-visible:after:ring-brand ${
            canonical ? "text-[17px]" : "text-[15px] text-zinc-600"
          }`}
          to={`/curricula/${fileName}`}
        >
          <RowTitle item={item} />
        </Link>
        <AsyncWorkIndicator documentId={id} />
        {item.generationStatus === "pending" ? (
          <button
            type="button"
            className="relative shrink-0 rounded border border-zinc-300 bg-white px-2 py-1 text-[11px] text-zinc-700 hover:bg-zinc-100 disabled:opacity-50"
            disabled={cancelling}
            onClick={handleCancel}
          >
            {cancelling ? "Cancelling…" : "Cancel"}
          </button>
        ) : (
          <button
            type="button"
            className={`relative ${action.brand ? ICON_BUTTON_BRAND : ICON_BUTTON}`}
            title={action.label}
            aria-label={action.label}
            disabled={busy}
            onClick={action.run}
          >
            {action.icon}
          </button>
        )}
      </span>
      {item.generationStatus === "pending" ? (
        <span className="flex w-full items-center gap-2">
          <span className="min-w-[10rem] flex-1">
            <RowGenerationProgress documentId={id} />
          </span>
          {cancelError !== undefined ? (
            <span className="text-[11px] text-red-700">{cancelError}</span>
          ) : null}
        </span>
      ) : null}
      <span className="flex min-w-0 items-center gap-3.5">
        <span className="shrink-0 rounded-full bg-zinc-100 px-2 py-0.5 text-[11px] font-medium text-zinc-600">
          {ROLE_LABEL[item.role]}
        </span>
        <PhaseTracker phase={item.phase} reworkPhase={item.reworkPhase} />
        <span className="ml-auto flex shrink-0 items-center gap-3.5">
          <span className="flex min-w-[4.5rem] flex-col items-end leading-tight">
            <span className="text-[10px] font-medium uppercase tracking-wide text-zinc-400">
              Updated
            </span>
            <span className="text-[13px] tabular-nums text-zinc-600">{since(item.updatedAt)}</span>
          </span>
          <span className="min-w-[6rem]">
            <VersionCell item={item} />
          </span>
        </span>
      </span>
    </li>
  );
};

// A level with experiments but no canonical is a real gap the index should surface — nothing in the
// schema forbids it, so the list is where it becomes visible.
const MissingCanonical = () => (
  <li className="flex items-center gap-2 rounded-2xl bg-amber-50 px-4 py-2.5 text-xs text-amber-800">
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      className="h-3.5 w-3.5 shrink-0"
      aria-hidden="true"
    >
      <path d="M12 9v4M12 17h.01M10.3 3.9 2 18a2 2 0 0 0 1.7 3h16.6a2 2 0 0 0 1.7-3L14 3.9a2 2 0 0 0-3.4 0z" />
    </svg>
    <span>No canonical document at this level — promote an experiment.</span>
  </li>
);

// ─── Grouping ─────────────────────────────────────────────────────────────────

const UNTAGGED = "untagged";

// Group by (l1 → l2), then by level within a pair. An untagged document has no pair to belong to, so
// it collects in its own group rather than being silently hidden or invented into one.
const groupByPair = (items: readonly CurriculumListItem[]) => {
  const pairs = new Map<string, CurriculumListItem[]>();
  for (const item of items) {
    const key = item.tags === undefined ? UNTAGGED : `${item.tags.l1 ?? "?"}>${item.tags.language}`;
    const bucket = pairs.get(key);
    if (bucket === undefined) pairs.set(key, [item]);
    else bucket.push(item);
  }
  return [...pairs.entries()]
    .sort(([a], [b]) => (a === UNTAGGED ? 1 : b === UNTAGGED ? -1 : a.localeCompare(b)))
    .map(([key, group]) => {
      const levels = new Map<string, CurriculumListItem[]>();
      for (const item of group) {
        const level = item.tags?.level ?? "—";
        const bucket = levels.get(level);
        if (bucket === undefined) levels.set(level, [item]);
        else bucket.push(item);
      }
      return {
        key,
        first: group[0],
        count: group.length,
        levels: [...levels.entries()].sort(([a], [b]) => levelRank(a) - levelRank(b)),
      };
    });
};

// ─── Filters ──────────────────────────────────────────────────────────────────

// `inRework` is its own value rather than a phase, because a reworking document is still
// `phase: published` — "show me what needs attention" cannot be said as a phase.
const PHASE_FILTERS = [...CURRICULUM_PHASES, "rework"] as const;
type PhaseFilter = (typeof PHASE_FILTERS)[number];

const PHASE_FILTER_LABEL: Record<PhaseFilter, string> = {
  structure: "Structure",
  exercises: "Exercises",
  published: "Published",
  rework: "In rework",
};

// Colour on the phase switches follows the same STATE palette the tracker uses; the two in-production
// stages share the in-progress tone on purpose — the text says which stage, the colour says what kind
// of state it is. Role switches stay neutral: the layout already carries role.
const PHASE_FILTER_CLASS: Record<PhaseFilter, string> = {
  structure: "bg-sky-100 text-sky-800",
  exercises: "bg-sky-100 text-sky-800",
  published: "bg-emerald-100 text-emerald-800",
  rework: "bg-amber-100 text-amber-800",
};

const DEFAULT_PHASES: ReadonlySet<PhaseFilter> = new Set(PHASE_FILTERS);
const DEFAULT_ROLES: ReadonlySet<CurriculumRole> = new Set(["canonical", "experiment"]);

const FilterChip = ({
  on,
  tone,
  label,
  onToggle,
}: {
  readonly on: boolean;
  readonly tone?: string | undefined;
  readonly label: string;
  readonly onToggle: () => void;
}) => (
  <button
    type="button"
    aria-pressed={on}
    onClick={onToggle}
    className={`rounded-full px-2.5 py-0.5 text-[11px] font-medium transition-colors ${
      on
        ? (tone ?? "bg-zinc-200 text-zinc-700")
        : "border border-dashed border-zinc-300 text-zinc-400 hover:text-zinc-600"
    }`}
  >
    {label}
  </button>
);

// ─── The import form ──────────────────────────────────────────────────────────

// Dropdowns for the identity a document must declare — shared by both creation paths in the "New
// Curriculum" modal below (import and generate). `l1` is required HERE (the UI rule) even though the
// schema tolerates its absence in principle: an empty string never decodes as a `CurriculumLanguage`,
// and that is what both paths lean on to gate their confirm button, with no separate presence check.
// (Since ADR-0111 the brand is a code SHAPE rather than a closed union — the empty string still fails
// it, so the gate is unchanged.)
//
// The language options come from `GET /api/language-packs/languages`, not from the compiled-in
// `CURRICULUM_LANGUAGES` table, so a pack uploaded five minutes ago is offered here. A language whose
// L2 pack is incomplete is rendered DISABLED with its first blocker, rather than omitted: a missing
// option leaves the human guessing, a disabled one with a reason tells them what to upload.
// Generate mode passes `levelOptions` to narrow and lock the same select rather than duplicating it:
// import keeps the full closed set (A0 included, nothing locked), generate swaps in
// `GENERATE_LEVELS` with prerequisite-locked entries disabled and labelled.
type LevelOption = { readonly value: string; readonly label: string; readonly disabled: boolean };

/**
 * The options for one language select.
 *
 * Falls back to the compiled-in display table while the readiness call is in flight, or if it fails —
 * the modal stays usable for the languages that shipped with the bundle rather than presenting an
 * empty select.
 */
const languageOptions = (
  languages: readonly LanguageReadiness[] | undefined,
  side: "l1" | "l2",
): readonly LevelOption[] => {
  const shipped: readonly LevelOption[] = CURRICULUM_LANGUAGES.map(({ code, name }) => ({
    value: code,
    label: name,
    disabled: false,
  }));
  const offered = (languages ?? [])
    .filter((language) => (side === "l1" ? language.isL1 : language.isL2))
    .map((language) => ({
      value: language.code,
      label: language.complete
        ? language.name
        : `${language.name} — ${language.blockers[0] ?? "pack incomplete"}`,
      // Only the L2 side is judged for completeness; the L1 side is checked at launch, where the
      // pair is known, so an L1 option is never disabled here.
      disabled: side === "l2" && !language.complete,
    }));
  // The fallback is per SIDE, not per call. A store that happens to hold no `L1_` directory would
  // otherwise render an empty "Learner's language" select and make the modal unusable — worse than
  // showing the languages this bundle shipped with.
  return offered.length === 0 ? shipped : offered;
};

const TagFields = ({
  level,
  l1,
  language,
  disabled,
  levelOptions,
  languages,
  onLevelChange,
  onL1Change,
  onLanguageChange,
}: {
  readonly level: string;
  readonly l1: string;
  readonly language: string;
  readonly disabled: boolean;
  readonly levelOptions?: readonly LevelOption[] | undefined;
  readonly languages?: readonly LanguageReadiness[] | undefined;
  readonly onLevelChange: (value: string) => void;
  readonly onL1Change: (value: string) => void;
  readonly onLanguageChange: (value: string) => void;
}) => (
  <>
    <label className="flex flex-col gap-1 text-[13px] text-zinc-700">
      <span className="text-[11px] font-semibold uppercase tracking-wide text-zinc-500">Level</span>
      <select
        className={`${TAG_SELECT} w-28`}
        value={level}
        disabled={disabled}
        onChange={(event) => onLevelChange(event.target.value)}
      >
        <option value="">Level…</option>
        {(
          levelOptions ?? CEFR_LEVELS.map((value) => ({ value, label: value, disabled: false }))
        ).map((option) => (
          <option key={option.value} value={option.value} disabled={option.disabled}>
            {option.label}
          </option>
        ))}
      </select>
    </label>
    <label className="flex flex-col gap-1 text-[13px] text-zinc-700">
      <span className="text-[11px] font-semibold uppercase tracking-wide text-zinc-500">L1</span>
      <select
        className={`${TAG_SELECT} w-44`}
        value={l1}
        disabled={disabled}
        onChange={(event) => onL1Change(event.target.value)}
      >
        <option value="">Speaks…</option>
        {languageOptions(languages, "l1").map((option) => (
          <option key={option.value} value={option.value} disabled={option.disabled}>
            {option.label}
          </option>
        ))}
      </select>
    </label>
    <label className="flex flex-col gap-1 text-[13px] text-zinc-700">
      <span className="text-[11px] font-semibold uppercase tracking-wide text-zinc-500">L2</span>
      <select
        className={`${TAG_SELECT} w-44`}
        value={language}
        disabled={disabled}
        onChange={(event) => onLanguageChange(event.target.value)}
      >
        <option value="">Learning…</option>
        {languageOptions(languages, "l2").map((option) => (
          <option key={option.value} value={option.value} disabled={option.disabled}>
            {option.label}
          </option>
        ))}
      </select>
    </label>
  </>
);

const ImportForm = ({
  busy,
  decoded,
  pendingImport,
  onPickFile,
  onSubmit,
  onCancel,
}: {
  readonly busy: boolean;
  readonly decoded: Either.Either<CurriculumTags, ParseResult.ParseError>;
  readonly pendingImport: { readonly name: string; readonly text: string } | undefined;
  readonly onPickFile: (event: React.ChangeEvent<HTMLInputElement>) => void;
  readonly onSubmit: (tags: CurriculumTags) => void;
  readonly onCancel: () => void;
}) =>
  pendingImport === undefined ? (
    <label className="inline-flex h-9 cursor-pointer items-center rounded-md border border-zinc-200 bg-white px-4 text-sm font-medium text-zinc-900 shadow-xs transition-colors hover:bg-zinc-100 has-[:disabled]:cursor-not-allowed has-[:disabled]:opacity-50">
      {busy ? "Reading…" : "Choose markdown file…"}
      <input
        type="file"
        accept=".md,.markdown,text/markdown"
        className="hidden"
        disabled={busy}
        onChange={onPickFile}
      />
    </label>
  ) : (
    <div className="flex flex-wrap items-center gap-2">
      <p className="w-full text-sm text-zinc-700">
        Importing <span className="font-medium">{pendingImport.name}</span>
      </p>
      <button
        type="button"
        className={ROW_BUTTON}
        disabled={busy || Either.isLeft(decoded)}
        onClick={() => {
          if (Either.isRight(decoded)) onSubmit(decoded.right);
        }}
      >
        {busy ? "Importing…" : "Import"}
      </button>
      <button type="button" className={ROW_BUTTON} disabled={busy} onClick={onCancel}>
        Cancel
      </button>
    </div>
  );

// ─── Generate-mode level gating ─────────────────────────────────────────────────

// `CEFR_LEVELS` MINUS `A0`: A0 has to stay in the shared literal for any historical A0-tagged
// document (the import side of this same modal still needs to offer it), but nothing should be
// GENERATED at A0 any more.
const GENERATE_LEVELS = CEFR_LEVELS.filter((level) => level !== "A0");

// The coarse CEFR bands in teaching order. A sublevel belongs to the band its prefix names
// (`A2.1` → `A2`), so the whole prerequisite rule below is expressed over six values rather than
// seventeen.
const CEFR_BANDS = ["A1", "A2", "B1", "B2", "C1", "C2"] as const;

const bandOf = (level: string): string => level.split(".")[0] ?? level;

// The band a locked level is waiting on, named in its own option label so the refusal explains
// itself without a tooltip. `undefined` only for a level with no prerequisite, which is never locked.
const previousBandOf = (level: string): string | undefined => {
  const index = (CEFR_BANDS as readonly string[]).indexOf(bandOf(level));
  return index <= 0 ? undefined : CEFR_BANDS[index - 1];
};

// A band counts as already taught for a language pair when the pair has a curriculum at the whole
// band (`A1`) or at BOTH of its sublevels (`A1.1` + `A1.2`) — one sublevel alone is half a band and
// does not unlock the next one.
const bandSatisfied = (band: string, levels: ReadonlySet<string>): boolean =>
  levels.has(band) || (levels.has(`${band}.1`) && levels.has(`${band}.2`));

// A level may be generated only once the band BEFORE its own is satisfied: with nothing at all for a
// pair only the A1 band (A1 / A1.1 / A1.2) is open; an A1 curriculum opens the A2 band, and so on.
// The generator drafts against the prior level's taught inventory, so generating out of order asks
// it to build on material that does not exist yet.
const levelUnlocked = (level: string, levels: ReadonlySet<string>): boolean => {
  const previous = previousBandOf(level);
  return previous === undefined || bandSatisfied(previous, levels);
};

// Which levels a pair already has, counting only canonical and experiment documents — the same set
// the index fetches by default (`DEFAULT_ROLES`), reused here rather than a second fetch. `archived`
// is deliberately excluded even if the visible filter widened to include it: it is out of rotation,
// and nothing should be built on top of it.
const COUNTING_ROLES: ReadonlySet<CurriculumRole> = new Set(["canonical", "experiment"]);

const levelsForPair = (
  curricula: readonly CurriculumListItem[],
  l1: string,
  language: string,
): ReadonlySet<string> =>
  new Set(
    curricula.flatMap((item) =>
      item.tags !== undefined &&
      COUNTING_ROLES.has(item.role) &&
      item.tags.l1 === l1 &&
      item.tags.language === language
        ? [item.tags.level as string]
        : [],
    ),
  );

// ─── New curriculum modal ──────────────────────────────────────────────────────

// U6 (CON-1316): replaces the old studio-admin-gated `/generate` route and the separate "New from
// markdown" file input with one entry point. The "Generate" toggle only appears for a studio-admin —
// same gate the old route used (`fetchLaunchAccess` fails closed) — and only avoids offering a button
// the server would refuse; `requireLaunchAccess` in `rest.ts` is the real enforcement. Generating costs
// real agent compute, so its confirm stays disabled until the warning is explicitly acknowledged.
type CreationMode = "import" | "generate";

const GenerateFields = ({
  busy,
  decoded,
  onSubmit,
  onCancel,
}: {
  readonly busy: boolean;
  readonly decoded: Either.Either<CurriculumTags, ParseResult.ParseError>;
  readonly onSubmit: (params: UnitPlanParams) => void;
  readonly onCancel: () => void;
}) => {
  const [ack, setAck] = useState(false);
  return (
    <div className="flex flex-col gap-3">
      <label className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
        <input
          type="checkbox"
          className="mt-0.5"
          checked={ack}
          disabled={busy}
          onChange={(event) => setAck(event.target.checked)}
        />
        <span>
          Generating a curriculum runs a full agent pipeline from scratch — real compute time and
          cost. Confirm you mean to start a new run.
        </span>
      </label>
      <div className="flex gap-2">
        <button
          type="button"
          className={ROW_BUTTON}
          disabled={busy || Either.isLeft(decoded) || !ack}
          onClick={() => {
            if (Either.isRight(decoded) && decoded.right.l1 !== undefined) {
              onSubmit({
                cefrLevel: decoded.right.level,
                l1: decoded.right.l1,
                l2: decoded.right.language,
              });
            }
          }}
        >
          {busy ? "Starting…" : "Start generation"}
        </button>
        <button type="button" className={ROW_BUTTON} disabled={busy} onClick={onCancel}>
          Cancel
        </button>
      </div>
    </div>
  );
};

const NewCurriculumModal = ({
  canGenerate,
  busy,
  pendingImport,
  error,
  onPickFile,
  onImportSubmit,
  onGenerate,
  onClose,
}: {
  readonly canGenerate: boolean;
  readonly busy: boolean;
  readonly pendingImport: { readonly name: string; readonly text: string } | undefined;
  readonly error: string | undefined;
  readonly onPickFile: (event: React.ChangeEvent<HTMLInputElement>) => void;
  readonly onImportSubmit: (tags: CurriculumTags) => void;
  readonly onGenerate: (params: UnitPlanParams) => void;
  readonly onClose: () => void;
}) => {
  const [mode, setMode] = useState<CreationMode>("import");
  const [level, setLevel] = useState("");
  const [l1, setL1] = useState("");
  const [language, setLanguage] = useState("");
  const decoded = Schema.decodeUnknownEither(CurriculumTags)({ level, language, l1 });

  // ADR-0111: which languages studio can actually offer, and whether each one's pack can carry a
  // run. Fetched on open rather than held in a module constant, because an upload can add one at any
  // time. `languagePackUploadActions.languages` already collapses a failure to an empty list, and
  // `languageOptions` falls back to the compiled-in table for that case.
  const [languages, setLanguages] = useState<readonly LanguageReadiness[] | undefined>(undefined);
  useEffect(() => {
    let cancelled = false;
    languagePackUploadActions.languages().then((list) => {
      if (!cancelled) setLanguages(list);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  // Generate-mode level gating (dropped in the CON-1316 merge that folded `/generate` into this
  // modal — restored here against the modal's own fields). Scoped to its own `listCurricula` call
  // rather than reusing the index's `curricula`: that array is narrowed to whatever `roleFilter` the
  // reviewer has picked (CON-1182), same reason `handlePromote` above doesn't trust it either. Only
  // a studio-admin ever sees generate mode, so this only fires for them; fails open (empty) on error
  // so a transient fetch failure degrades to "nothing locked" rather than blocking generation outright.
  const actions = useGateActions();
  const [existingCurricula, setExistingCurricula] = useState<readonly CurriculumListItem[]>([]);
  useEffect(() => {
    if (!canGenerate) return;
    let cancelled = false;
    actions.listCurricula([...COUNTING_ROLES]).then(
      (list) => {
        if (!cancelled) setExistingCurricula(list);
      },
      () => {
        if (!cancelled) setExistingCurricula([]);
      },
    );
    return () => {
      cancelled = true;
    };
  }, [canGenerate, actions]);

  const existingLevels = useMemo(
    () => levelsForPair(existingCurricula, l1, language),
    [existingCurricula, l1, language],
  );
  // The chosen L2's inventory coverage. `undefined` while readiness loads (or for a language it does
  // not list), which locks nothing — the server's own check still refuses a gap at launch.
  const readiness = useMemo(
    () => languages?.find((candidate) => candidate.code === language && candidate.isL2),
    [languages, language],
  );
  const gapsFor = useCallback(
    (candidate: string) =>
      readiness === undefined ? [] : missingInventories(readiness, candidate),
    [readiness],
  );
  const unlockedLevels = useMemo(
    () =>
      GENERATE_LEVELS.filter(
        (candidate) => levelUnlocked(candidate, existingLevels) && gapsFor(candidate).length === 0,
      ),
    [existingLevels, gapsFor],
  );
  // The level worth generating next for this pair: the lowest unlocked one it does not already
  // have, falling back to the highest unlocked when it has them all.
  const frontier = useMemo(
    () =>
      unlockedLevels.find((candidate) => !existingLevels.has(candidate)) ?? unlockedLevels.at(-1),
    [unlockedLevels, existingLevels],
  );
  // Follows the frontier until the reviewer manually touches Level — not just "while the current
  // selection is invalid": A1 is unlocked for every pair (no band comes before it), so a default of
  // "" snapping once to A1 would otherwise never move again as the real pair's inventory loads,
  // stranding every pair on A1 regardless of what it actually has. Once touched, a still-valid manual
  // choice is never overridden; an invalid one (the reviewer picked, then changed pair) still snaps.
  const levelTouched = useRef(false);
  useEffect(() => {
    if (mode !== "generate" || frontier === undefined) return;
    if (levelTouched.current && (unlockedLevels as readonly string[]).includes(level)) return;
    setLevel(frontier);
  }, [mode, unlockedLevels, frontier, level]);
  const generateLevelOptions: readonly LevelOption[] = useMemo(
    () =>
      GENERATE_LEVELS.map((value) => {
        const locked = !levelUnlocked(value, existingLevels);
        const gaps = gapsFor(value);
        return {
          value,
          label: locked
            ? `${value} — needs ${previousBandOf(value)} first`
            : gaps.length > 0
              ? `${value} — needs ${gaps.join(" + ")} inventory`
              : value,
          disabled: locked || gaps.length > 0,
        };
      }),
    [existingLevels, gapsFor],
  );
  // One line per level the pack cannot carry yet, naming the file to fill — the option label alone
  // says what is missing but not where to add it.
  const inventoryGaps = useMemo(
    () =>
      GENERATE_LEVELS.flatMap((value) => {
        const gaps = gapsFor(value);
        return gaps.length === 0 ? [] : [missingInventoryMessage(language, value, gaps)];
      }),
    [gapsFor, language],
  );

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="New curriculum"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 px-4"
    >
      <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-[0_8px_30px_rgba(0,0,0,0.12)]">
        <div className="mb-4 flex items-baseline justify-between">
          <h2 className="font-serif text-xl tracking-[-0.01em] text-zinc-900">New curriculum</h2>
          <button
            type="button"
            className="text-sm text-zinc-500 hover:text-zinc-800"
            disabled={busy}
            onClick={onClose}
          >
            Close
          </button>
        </div>

        <div className="mb-4 flex flex-col gap-2">
          <TagFields
            level={level}
            l1={l1}
            language={language}
            disabled={busy}
            levelOptions={mode === "generate" ? generateLevelOptions : undefined}
            languages={languages}
            onLevelChange={(value) => {
              levelTouched.current = true;
              setLevel(value);
            }}
            onL1Change={setL1}
            onLanguageChange={setLanguage}
          />
        </div>

        {canGenerate ? (
          <div className="mb-4 flex gap-1.5">
            <FilterChip
              on={mode === "import"}
              label="From file"
              onToggle={() => setMode("import")}
            />
            <FilterChip
              on={mode === "generate"}
              label="Generate"
              onToggle={() => setMode("generate")}
            />
          </div>
        ) : null}

        {mode === "generate" ? (
          <p className="mb-3 text-[11px] text-zinc-500">
            A level opens once the band before it exists for this pair — a whole band (A1) or both
            of its sublevels (A1.1 + A1.2). Everything above that stays locked.
          </p>
        ) : null}

        {mode === "generate" && inventoryGaps.length > 0 ? (
          <div className="mb-3 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-[11px] text-amber-900">
            <p className="mb-1 font-semibold">
              Some levels need inventories before you can pick them:
            </p>
            <ul className="list-disc pl-4">
              {inventoryGaps.map((message) => (
                <li key={message}>{message}</li>
              ))}
            </ul>
          </div>
        ) : null}

        {error === undefined ? null : (
          <p className="mb-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
            {error}
          </p>
        )}

        {mode === "import" ? (
          <ImportForm
            busy={busy}
            decoded={decoded}
            pendingImport={pendingImport}
            onPickFile={onPickFile}
            onSubmit={onImportSubmit}
            onCancel={onClose}
          />
        ) : (
          <GenerateFields busy={busy} decoded={decoded} onSubmit={onGenerate} onCancel={onClose} />
        )}
      </div>
    </div>
  );
};

// ─── The page ─────────────────────────────────────────────────────────────────

export const CurriculaIndex = () => {
  const actions = useGateActions();
  const navigate = useNavigate();
  const [curricula, setCurricula] = useState<readonly CurriculumListItem[]>([]);
  const [l2Filter, setL2Filter] = useState<string>("");
  const [phaseFilter, setPhaseFilter] = useState<ReadonlySet<PhaseFilter>>(DEFAULT_PHASES);
  const [roleFilter, setRoleFilter] = useState<ReadonlySet<CurriculumRole>>(DEFAULT_ROLES);
  const [busy, setBusy] = useState(false);
  const [pendingImport, setPendingImport] = useState<
    { readonly name: string; readonly text: string } | undefined
  >(undefined);
  const [error, setError] = useState<string | undefined>(undefined);
  const [newCurriculumOpen, setNewCurriculumOpen] = useState(false);
  // Same gate the "New Curriculum" modal's Generate toggle uses: generating a curriculum is a
  // studio-admin action, so the affordance is absent rather than present-but-disabled.
  // `fetchLaunchAccess` fails closed, so an unresolved role hides the toggle. `requireLaunchAccess`
  // in `rest.ts` is the actual enforcement point; this only avoids offering a button that would be
  // refused.
  const [viewerRole, setViewerRole] = useState<ViewerRoleWire | undefined>(undefined);
  // A successful create navigates away, so an action's `.then` can resolve after unmount.
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  // `roleFilter` goes to the server (CON-1182): a document whose role isn't selected is never
  // queried or built, not just hidden after the fact. Phase and language stay client-side filters
  // over the (already role-narrowed) result.
  // Guards against out-of-order responses: two `curriculum-changed` WS frames in quick succession
  // can fire two overlapping `listCurricula` calls whose responses resolve out of order. Without a
  // sequence check, an earlier call's response landing after a later one would silently regress the
  // list to a stale snapshot.
  const reloadSeq = useRef(0);
  const reload = useMemo(
    () => () => {
      const seq = ++reloadSeq.current;
      return actions.listCurricula([...roleFilter]).then((list) => {
        if (mounted.current && seq === reloadSeq.current) setCurricula(list);
      });
    },
    [actions, roleFilter],
  );
  // Routes through `reload` rather than a second independent `listCurricula` call, so this effect's
  // own response is subject to the same `reloadSeq` guard as a WS-triggered reload (finding #14): a
  // bespoke `live` flag here only re-covered what `reload`'s `mounted.current` check already does,
  // while leaving this call free to race and lose to (or overwrite) a WS-triggered `reload()`.
  useEffect(() => {
    reload();
  }, [reload]);

  // U7 (CON-1316): `/ws/curricula` is the list-level sibling of the per-document sync socket
  // (`ws-client.ts`) — no snapshot, no drafts, just "something changed, re-fetch" (see its module doc
  // in `packages/core/src/sync.ts` and `curriculaWsRoute` in `apps/server/src/ws.ts`). It is
  // deliberately NOT built on `ws-client.ts`'s heavier `Socket.makeWebSocket` + jittered-backoff
  // machinery — that machinery exists to drive the collab document's patch/resync protocol, which
  // this single-frame-kind socket has none of. A plain reconnecting browser `WebSocket` is the whole
  // client. `reloadRef` (rather than `reload` itself in the effect's dependency array) is what keeps
  // the connection open for the component's full lifetime — a `roleFilter` change must not tear down
  // and reopen the socket, only change which callback a message dispatches to.
  const reloadRef = useRef(reload);
  useEffect(() => {
    reloadRef.current = reload;
  }, [reload]);

  useEffect(() => {
    let cancelled = false;
    let socket: WebSocket | undefined;
    let attempt = 0;
    let retryTimer: ReturnType<typeof setTimeout> | undefined;
    const decode = Schema.decodeUnknownEither(Schema.parseJson(CurriculumChanged));

    const connect = () => {
      if (cancelled) return;
      const url = new URL("/ws/curricula", window.location.origin);
      url.protocol = url.protocol === "https:" ? "wss:" : "ws:";
      const ws = new WebSocket(url);
      socket = ws;
      ws.addEventListener("open", () => {
        // Finding #2: a connection gap — however it resolved — means missed `curriculum-changed`
        // frames are possible, so treat "just (re)connected" the same as "a change was announced"
        // and catch up. This is a bare reload, not the team's more rigorous seq-anchored catch-up
        // pattern (see docs/solutions/2026-06-22-in-document-tokens-to-event-stream-projection.md) —
        // accepted as the minimum fix here.
        attempt = 0;
        reloadRef.current();
      });
      ws.addEventListener("message", (event) => {
        const decoded = decode(event.data);
        if (Either.isRight(decoded)) {
          reloadRef.current();
        } else {
          const reason = ParseResult.ArrayFormatter.formatErrorSync(decoded.left)
            .map((issue) => issue.message)
            .join("; ");
          console.warn("[ws] undecodable /ws/curricula frame dropped:", reason);
        }
      });
      ws.addEventListener("close", () => {
        if (cancelled) return;
        attempt += 1;
        retryTimer = setTimeout(connect, Math.min(30_000, 1000 * 2 ** attempt));
      });
      ws.addEventListener("error", () => ws.close());
    };
    connect();

    return () => {
      cancelled = true;
      if (retryTimer !== undefined) clearTimeout(retryTimer);
      socket?.close();
    };
  }, []);

  const visible = useMemo(
    () =>
      curricula.filter((item) => {
        // A reworking document answers to the rework switch rather than its (still published) phase,
        // so "what needs attention" is one click.
        const key: PhaseFilter = item.reworkPhase !== undefined ? "rework" : item.phase;
        if (!phaseFilter.has(key)) return false;
        return l2Filter === "" || item.tags?.language === l2Filter;
      }),
    [curricula, phaseFilter, l2Filter],
  );

  const run = (work: Promise<{ readonly _tag: string; readonly message?: string }>) => {
    if (busy) return;
    setError(undefined);
    setBusy(true);
    work
      .then((result) => {
        if (!mounted.current) return undefined;
        if (result._tag !== "ok") {
          setError(result.message);
          return undefined;
        }
        return reload();
      })
      .finally(() => {
        if (mounted.current) setBusy(false);
      });
  };

  const handleClone = (sourceId: DocumentId) => {
    if (busy) return;
    setError(undefined);
    setBusy(true);
    actions
      .cloneCurriculum(sourceId)
      .then((result) => {
        if (!mounted.current) return;
        // Open the new copy so the Expert lands on the thing they just made.
        if (result._tag === "ok") navigate(`/${result.documentId}`);
        else setError(result.message);
      })
      .finally(() => {
        if (mounted.current) setBusy(false);
      });
  };

  // Promotion is atomic in intent: the experiment becomes canonical and names the incumbent it
  // replaced, and that incumbent is archived so the level keeps exactly one canonical. Looked up via
  // its own scoped fetch, not `curricula`: that array is now narrowed to `roleFilter` (CON-1182), so
  // if the reviewer has "canonical" unchecked it would no longer hold the incumbent to demote.
  const handlePromote = (item: CurriculumListItem) => {
    const promoted = DocumentId.make(item.documentId);
    run(
      actions.listCurricula(["canonical"]).then((canonicalDocs) => {
        const incumbent = canonicalDocs.find(
          (other) =>
            other.tags?.level === item.tags?.level &&
            other.tags?.language === item.tags?.language &&
            other.tags?.l1 === item.tags?.l1,
        );
        return actions
          .setCurriculumRole(
            promoted,
            "canonical",
            incumbent === undefined ? undefined : DocumentId.make(incumbent.documentId),
          )
          .then(async (result) => {
            if (result._tag !== "ok" || incumbent === undefined) return result;
            return actions.setCurriculumRole(DocumentId.make(incumbent.documentId), "archived");
          });
      }),
    );
  };

  const handleImport = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (file === undefined || busy || pendingImport !== undefined) return;
    setError(undefined);
    file.text().then((text) => {
      if (mounted.current) setPendingImport({ name: file.name, text });
    });
  };

  const confirmImport = (tags: CurriculumTags) => {
    if (pendingImport === undefined || busy) return;
    setError(undefined);
    setBusy(true);
    actions
      .importDocument(pendingImport.text, tags)
      .then((result) => {
        if (!mounted.current) return;
        if (result._tag === "ok") {
          setPendingImport(undefined);
          setNewCurriculumOpen(false);
          navigate(`/${result.documentId}`);
        } else setError(result.message);
      })
      .finally(() => {
        if (mounted.current) setBusy(false);
      });
  };

  // Unlike import (which navigates to the minted document), a successful generate leaves the Expert
  // on the index — the document is only a pending placeholder until the run lands — so this closes
  // the modal and reloads the list to pick up the new `generationStatus: "pending"` row instead.
  const handleGenerate = (params: UnitPlanParams) => {
    if (busy) return;
    setError(undefined);
    setBusy(true);
    unitPlanActions
      .start(params)
      .then((result) => {
        if (!mounted.current) return;
        if (result._tag === "started") {
          setPendingImport(undefined);
          setNewCurriculumOpen(false);
          return reload();
        }
        setError(result.message);
        return undefined;
      })
      .finally(() => {
        if (mounted.current) setBusy(false);
      });
  };

  const closeNewCurriculum = () => {
    if (busy) return;
    setNewCurriculumOpen(false);
    setPendingImport(undefined);
    setError(undefined);
  };

  const toggle = <T,>(set: ReadonlySet<T>, value: T): ReadonlySet<T> => {
    const next = new Set(set);
    if (next.has(value)) next.delete(value);
    else next.add(value);
    return next;
  };

  useEffect(() => {
    let cancelled = false;
    fetchLaunchAccess().then((resolved) => {
      if (!cancelled) setViewerRole(resolved);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const groups = useMemo(() => groupByPair(visible), [visible]);
  const taughtLanguages = useMemo(
    () => [...new Set(curricula.flatMap((c) => (c.tags ? [c.tags.language] : [])))].sort(),
    [curricula],
  );

  return (
    <main className="mx-auto max-w-5xl px-6 py-12">
      <div className="mb-6 flex items-end justify-between gap-6">
        <div>
          <h1 className="mb-1.5 font-serif text-3xl tracking-[-0.01em] text-zinc-900">Curricula</h1>
          <p className="max-w-[52ch] text-[13px] text-zinc-600">
            Every level of every language pair. One canonical document per level, advancing
            structural review → exercise review → published. Publishing pins a version; head keeps
            moving until you publish again.
          </p>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <button
            type="button"
            className="inline-flex h-9 items-center rounded-md border border-brand bg-brand px-4 text-sm font-medium text-white shadow-xs transition-colors hover:bg-brand/90"
            onClick={() => {
              setError(undefined);
              setNewCurriculumOpen(true);
            }}
          >
            New Curriculum
          </button>
          <Link
            to="/language-packs"
            className="inline-flex h-9 items-center rounded-md border border-zinc-200 bg-white px-4 text-sm font-medium text-zinc-900 shadow-xs transition-colors hover:bg-zinc-100"
          >
            Language packs
          </Link>
          <Link
            to="/reference-guides"
            className="inline-flex h-9 items-center rounded-md border border-zinc-200 bg-white px-4 text-sm font-medium text-zinc-900 shadow-xs transition-colors hover:bg-zinc-100"
          >
            Reference guides
          </Link>
        </div>
      </div>

      {error === undefined || newCurriculumOpen ? null : (
        <p className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-2 text-sm text-red-700">
          {error}
        </p>
      )}

      {newCurriculumOpen ? (
        <NewCurriculumModal
          canGenerate={viewerRole === "studio-admin"}
          busy={busy}
          pendingImport={pendingImport}
          error={error}
          onPickFile={handleImport}
          onImportSubmit={confirmImport}
          onGenerate={handleGenerate}
          onClose={closeNewCurriculum}
        />
      ) : null}

      <div className="mb-7 flex flex-wrap items-center gap-x-6 gap-y-3">
        <div className="flex items-center gap-2">
          <span className="text-[11px] font-semibold uppercase tracking-wide text-zinc-500">
            Learning
          </span>
          <select
            aria-label="Filter by language taught"
            className={TAG_SELECT}
            value={l2Filter}
            onChange={(event) => setL2Filter(event.target.value)}
          >
            <option value="">All languages</option>
            {taughtLanguages.map((code) => (
              <option key={code} value={code}>
                {languageName(code)}
              </option>
            ))}
          </select>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-[11px] font-semibold uppercase tracking-wide text-zinc-500">
            Phase
          </span>
          <div className="flex flex-wrap gap-1.5">
            {PHASE_FILTERS.map((value) => (
              <FilterChip
                key={value}
                on={phaseFilter.has(value)}
                tone={PHASE_FILTER_CLASS[value]}
                label={PHASE_FILTER_LABEL[value]}
                onToggle={() => setPhaseFilter((current) => toggle(current, value))}
              />
            ))}
          </div>
        </div>
        <div className="ml-auto flex items-center gap-2">
          <span className="text-[11px] font-semibold uppercase tracking-wide text-zinc-500">
            Role
          </span>
          <div className="flex flex-wrap gap-1.5">
            {CURRICULUM_ROLES.map((value) => (
              <FilterChip
                key={value}
                on={roleFilter.has(value)}
                label={ROLE_LABEL[value]}
                onToggle={() => setRoleFilter((current) => toggle(current, value))}
              />
            ))}
          </div>
        </div>
      </div>

      {groups.length === 0 ? (
        <p className="py-12 text-center text-[13px] text-zinc-400">
          Nothing matches these filters.
        </p>
      ) : (
        groups.map(({ key, first, count, levels }) => (
          <section key={key} className="mb-10">
            <div className="mb-3 flex items-baseline gap-3 border-b border-zinc-200 pb-2">
              {key === UNTAGGED || first?.tags === undefined ? (
                <span className="font-serif text-lg tracking-[-0.01em] text-zinc-900">
                  Untagged
                </span>
              ) : (
                <>
                  <span className="text-[11px] font-semibold uppercase tracking-[0.08em] text-zinc-500">
                    {first.tags.l1 === undefined ? "—" : l1Shorthand(first.tags.l1)} →{" "}
                    {first.tags.language}
                  </span>
                  <span className="font-serif text-lg tracking-[-0.01em] text-zinc-900">
                    {first.tags.l1 === undefined ? "—" : languageName(first.tags.l1)} →{" "}
                    {languageName(first.tags.language)}
                  </span>
                </>
              )}
              <span className="ml-auto text-[11px] font-medium uppercase tracking-wide tabular-nums text-zinc-400">
                {levels.length} {levels.length === 1 ? "level" : "levels"} · {count}{" "}
                {count === 1 ? "doc" : "docs"}
              </span>
            </div>
            {levels.map(([level, items]) => {
              const canonical = items.filter((item) => item.role === "canonical");
              const rest = items.filter((item) => item.role !== "canonical");
              return (
                <div key={level} className="mb-2 grid grid-cols-[3.25rem_1fr] items-start gap-3">
                  <div className="mt-3 rounded-md bg-zinc-200 py-1 text-center text-xs font-medium tabular-nums text-zinc-600">
                    {level}
                  </div>
                  <ul className="flex min-w-0 flex-col gap-1.5">
                    {canonical.length === 0 ? <MissingCanonical /> : null}
                    {canonical.map((item) => (
                      <CurriculumRow
                        key={item.documentId}
                        item={item}
                        canonical
                        onClone={handleClone}
                        onPromote={handlePromote}
                        onPublish={(id) => run(actions.publishCurriculum(id))}
                        busy={busy}
                      />
                    ))}
                    {rest.map((item) => (
                      <CurriculumRow
                        key={item.documentId}
                        item={item}
                        canonical={false}
                        onClone={handleClone}
                        onPromote={handlePromote}
                        onPublish={(id) => run(actions.publishCurriculum(id))}
                        busy={busy}
                      />
                    ))}
                  </ul>
                </div>
              );
            })}
          </section>
        ))
      )}
    </main>
  );
};
